"use client";

import { useState } from "react";
import { useTranslation } from "react-i18next";
import toast from "react-hot-toast";
import {
  ChevronsUpDown,
  CreditCard,
  LogOut,
} from "lucide-react";

import { Avatar, AvatarFallback, AvatarImage } from "@/components/ui/avatar";
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuGroup,
  DropdownMenuItem,
  DropdownMenuLabel,
  DropdownMenuSeparator,
  DropdownMenuTrigger,
} from "@/components/ui/dropdown-menu";
import {
  SidebarMenu,
  SidebarMenuButton,
  SidebarMenuItem,
} from "@/components/ui/sidebar";
import { useUserStore } from "../store/userStore";
import { usePenpal, isInIframe } from "../hooks/usePenpal";
import { generateBillingLink, apiClient } from "../services/api";
import { useNavigate } from "react-router-dom";

export function NavUser() {
  const { t } = useTranslation();
  const { account, properties, logout } = useUserStore();
  const { openExternalUrl } = usePenpal();
  const navigate = useNavigate();
  const [isLoading, setIsLoading] = useState(false);
  const inIframe = isInIframe();

  const openBilling = async (e: React.MouseEvent<HTMLButtonElement>) => {
    e.preventDefault();
    setIsLoading(true);
    try {
      const data = await generateBillingLink();
      if (data.url) {
        toast.success(t('nav.opening'), {
          position: "top-center",
          duration: 3000,
        });
        openExternalUrl(data.url);
      } else {
        throw new Error("No billing URL received");
      }
    } catch (error) {
      console.error("Billing error:", error);
      toast.error(
        t('nav.billingError'),
        {
          position: "top-center",
          duration: 5000,
        }
      );
    } finally {
      setIsLoading(false);
    }
  };

  // Prepare user data
  const userData = {
    name: account?.fullname || t('nav.userFallback'),
    email: account?.email || "",
    avatar: "", // You can add avatar URL if available
  };

  return (
    <SidebarMenu>
      <SidebarMenuItem>
        <DropdownMenu>
          <DropdownMenuTrigger asChild>
            {/* <Button
                    variant="ghost"
                   className="data-[state=open]:bg-sidebar-accent data-[state=open]:text-sidebar-accent-foreground"
                  > */}
            <SidebarMenuButton
              size="lg"
              className="data-[state=open]:bg-sidebar-accent data-[state=open]:text-sidebar-accent-foreground"
            >
              <Avatar className="h-8 w-8 rounded-lg">
                <AvatarImage src={userData.avatar} alt={userData.name} />
                <AvatarFallback className="rounded-lg">
                  {userData.name.slice(0, 2).toUpperCase()}
                </AvatarFallback>
              </Avatar>
              <div className="grid flex-1 text-left text-sm leading-tight">
                <span className="truncate font-semibold">{userData.name}</span>
                <span className="truncate text-xs">{userData.email}</span>
              </div>
              <ChevronsUpDown className="ml-auto size-4" />
              {/* </Button> */}
            </SidebarMenuButton>
          </DropdownMenuTrigger>
          <DropdownMenuContent
            className="w-[--radix-dropdown-menu-trigger-width] min-w-56 rounded-lg"
            side="bottom"
            align="end"
            sideOffset={4}
          >
            <DropdownMenuLabel className="p-0 font-normal">
              <div className="flex items-center gap-2 px-1 py-1.5 text-left text-sm">
                <Avatar className="h-8 w-8 rounded-lg">
                  <AvatarImage src={userData.avatar} alt={userData.name} />
                  <AvatarFallback className="rounded-lg">{userData.name.slice(0, 2).toUpperCase()}</AvatarFallback>
                </Avatar>
                <div className="grid flex-1 text-left text-sm leading-tight">
                  <span className="truncate font-semibold">
                    {userData.name}
                  </span>
                  <span className="truncate text-xs">{userData.email}</span>
                </div>
              </div>
            </DropdownMenuLabel>
            <DropdownMenuSeparator />
            <DropdownMenuGroup>
              {properties?.portal?.settings?.canManageBilling && (
                <DropdownMenuItem onClick={openBilling} disabled={isLoading}>
                  <CreditCard />
                  {isLoading ? t('nav.opening') : t('nav.billingSettings')}
                </DropdownMenuItem>
              )}
              {!inIframe && (
                <>
                  <DropdownMenuSeparator />
                  <DropdownMenuItem
                    onClick={async () => {
                      await apiClient.logout();
                      logout();
                      navigate('/login');
                    }}
                  >
                    <LogOut />
                    {t('nav.logOut')}
                  </DropdownMenuItem>
                </>
              )}
            </DropdownMenuGroup>
          </DropdownMenuContent>
        </DropdownMenu>
      </SidebarMenuItem>
    </SidebarMenu>
  );
}
